import { useEffect, useState, useRef } from "react";
import { useClub } from "@/contexts/ClubContext";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, Clock, ChevronDown, Download, Share2, Check, History } from "lucide-react";
import { de } from "date-fns/locale";
import { format } from "date-fns";
import { resolveImageUrl } from "@/utils/imageUrl";
import { LinkifyText } from "@/components/LinkifyText";
import { ImageLightbox } from "@/components/ImageLightbox";
import { usePageTitle } from "@/hooks/usePageTitle";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CalendarPage() {
  const { activeClub, clubs, getClubAccent, setActiveClub } = useClub();
  usePageTitle("Veranstaltungskalender");
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [showPast, setShowPast] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const highlightRef = useRef(null);

  const eventId = searchParams.get("event");

  useEffect(() => {
    const params = activeClub ? `?club_id=${activeClub}` : "";
    axios.get(`${API}/events${params}`).then(r => {
      setEvents(r.data);
      if (eventId) {
        const target = r.data.find(e => e.id === eventId);
        if (target) {
          setSelectedDate(new Date(target.date));
          setExpandedEvent(eventId);
          setTimeout(() => {
            const el = document.querySelector(`[data-testid="calendar-event-${eventId}"]`);
            if (el) {
              el.scrollIntoView({ behavior: "smooth", block: "center" });
              el.classList.add("ring-2", "ring-primary");
              highlightRef.current = el;
              setTimeout(() => el.classList.remove("ring-2", "ring-primary"), 10000);
            }
          }, 300);
        }
      }
    }).catch(() => {});
  }, [activeClub, eventId]);

  const eventDates = events.map(e => new Date(e.date));
  const selectedDateStr = format(selectedDate, "yyyy-MM-dd");
  const dayEvents = events.filter(e => e.date === selectedDateStr);
  const todayStr = format(new Date(), "yyyy-MM-dd");
  const upcomingEvents = events.filter(e => e.date >= todayStr);
  const pastEvents = events.filter(e => e.date < todayStr).sort((a, b) => b.date.localeCompare(a.date));
  const allTermineEvents = showPast ? pastEvents : upcomingEvents;

  return (
    <div className="py-12 md:py-24 px-4 md:px-8" data-testid="calendar-page">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight mb-4" style={{ fontFamily: 'Oswald' }}>
          Veranstaltungs<span style={{ color: activeClub ? getClubAccent(activeClub) : '#fbbf24' }}>kalender</span>
        </h1>
        <p className="text-base md:text-lg text-muted-foreground mb-8">Alle Termine auf einen Blick</p>

        {/* Club Filter */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="calendar-club-filter">
          <button onClick={() => setActiveClub(null)}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border ${!activeClub ? 'border-primary bg-primary text-primary-foreground' : 'border-white/10 text-muted-foreground hover:text-foreground'}`}
            data-testid="filter-all">
            Alle
          </button>
          {clubs.map(club => (
            <button key={club.id} onClick={() => setActiveClub(activeClub === club.id ? null : club.id)}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border ${activeClub === club.id ? '' : 'border-white/10 text-muted-foreground hover:text-foreground'}`}
              style={activeClub === club.id ? { borderColor: club.accent, backgroundColor: club.accent, color: '#09090b' } : {}}
              data-testid={`filter-${club.id}`}>
              {club.name}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8">
          {/* Calendar */}
          <div className="bg-card border border-white/5 p-4 self-start" data-testid="calendar-widget">
            <Calendar mode="single" selected={selectedDate} onSelect={(d) => d && setSelectedDate(d)}
              locale={de}
              modifiers={{ event: eventDates }}
              modifiersStyles={{ event: { fontWeight: 'bold', color: activeClub ? getClubAccent(activeClub) : '#fbbf24' } }}
              className="w-full" />
          </div>

          {/* Events List */}
          <div>
            <h2 className="text-xl font-bold uppercase tracking-tight mb-4" style={{ fontFamily: 'Oswald' }}>
              {format(selectedDate, "d. MMMM yyyy", { locale: de })}
            </h2>
            {dayEvents.length === 0 ? (
              <p className="text-sm text-muted-foreground">Keine Veranstaltungen an diesem Tag.</p>
            ) : (
              <div className="space-y-4">
                {dayEvents.map(event => {
                  const isOpen = expandedEvent === event.id;
                  const imgSrc = resolveImageUrl(event.image_url);
                  const hasDetails = event.description || imgSrc;
                  return (
                    <div key={event.id} className="bg-card border border-white/5 overflow-hidden"
                      data-testid={`calendar-event-${event.id}`}>
                      <button onClick={() => hasDetails ? setExpandedEvent(isOpen ? null : event.id) : navigate(`/event/${event.id}`)}
                        className={`w-full p-5 text-left ${hasDetails ? 'hover:bg-white/[0.02] cursor-pointer' : 'cursor-default'} transition-colors`}
                        data-testid={`event-toggle-${event.id}`}>
                        <div className="flex items-start gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge style={{ backgroundColor: getClubAccent(event.club_id), color: '#09090b' }}
                                className="text-xs font-bold uppercase">
                                {clubs.find(c => c.id === event.club_id)?.name || event.club_id}
                              </Badge>
                              {event.featured && <Badge variant="outline" className="text-xs">Featured</Badge>}
                            </div>
                            <h3 className="text-lg font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>
                              {event.title}
                            </h3>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                              <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {event.date}</span>
                              {event.time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {event.time}{event.end_time ? ` - ${event.end_time}` : ''}</span>}
                              {hasDetails && !isOpen && <span className="text-primary ml-auto">mehr Infos</span>}
                            </div>
                          </div>
                          {hasDetails && <ChevronDown className={`w-5 h-5 text-muted-foreground shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />}
                        </div>
                      </button>
                      {isOpen && (
                        <div className="px-5 pb-5 border-t border-white/5 pt-4">
                          {imgSrc && (
                            <img src={imgSrc} alt={event.title}
                              onClick={() => setLightboxSrc(imgSrc)}
                              onError={e => { e.target.style.display = 'none'; }}
                              className="w-full max-h-80 object-contain bg-black/30 border border-white/10 mb-4 cursor-pointer hover:opacity-90 transition-opacity"
                              data-testid={`calendar-event-img-${event.id}`} />
                          )}
                          {event.description && (
                            <p className="text-sm text-muted-foreground whitespace-pre-line mb-4">
                              <LinkifyText text={event.description} />
                            </p>
                          )}
                          <div className="flex items-center gap-4">
                            <a href={`${API}/events/${event.id}/ical`} download
                              className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                              data-testid={`calendar-ical-${event.id}`}>
                              <Download className="w-3 h-3" /> Zum Kalender hinzufügen
                            </a>
                            <button onClick={(e) => {
                                e.stopPropagation();
                                const shareUrl = `${window.location.origin}/api/share/event/${event.id}`;
                                navigator.clipboard.writeText(shareUrl);
                                setCopiedId(event.id);
                                setTimeout(() => setCopiedId(null), 2000);
                              }}
                              className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                              data-testid={`share-event-${event.id}`}>
                              {copiedId === event.id ? <><Check className="w-3 h-3" /> Link kopiert!</> : <><Share2 className="w-3 h-3" /> Link zum Teilen</>}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* All Events */}
            <div className="flex items-center justify-between mt-10 mb-4">
              <h2 className="text-xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>
                {showPast ? 'Vergangene Termine' : 'Kommende Termine'}
              </h2>
              <button
                onClick={() => setShowPast(!showPast)}
                className={`flex items-center gap-2 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border transition-colors ${
                  showPast ? 'border-primary bg-primary/10 text-primary' : 'border-white/10 text-muted-foreground hover:text-foreground hover:border-white/20'
                }`}
                data-testid="toggle-past-events-btn"
              >
                <History className="w-3.5 h-3.5" />
                {showPast ? 'Kommende anzeigen' : 'Vergangene anzeigen'}
                {!showPast && pastEvents.length > 0 && (
                  <span className="ml-1 bg-white/10 px-1.5 py-0.5 text-[10px]">{pastEvents.length}</span>
                )}
              </button>
            </div>
            {allTermineEvents.length === 0 ? (
              <p className="text-sm text-muted-foreground">{showPast ? 'Keine vergangenen Termine vorhanden.' : 'Keine kommenden Termine vorhanden.'}</p>
            ) : (
            <div className="space-y-3">
              {allTermineEvents.map(event => (
                <div key={event.id} className="bg-card border border-white/5 p-4 flex items-center gap-4 cursor-pointer hover:border-white/20 transition-colors duration-200"
                  onClick={() => {
                    setSelectedDate(new Date(event.date));
                    setExpandedEvent(event.id);
                    setTimeout(() => {
                      const el = document.querySelector(`[data-testid="calendar-event-${event.id}"]`);
                      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                    }, 100);
                  }}
                  data-testid={`event-list-${event.id}`}>
                  <div className="w-1 h-10 rounded-full" style={{ backgroundColor: getClubAccent(event.club_id) }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold uppercase tracking-tight truncate" style={{ fontFamily: 'Oswald' }}>{event.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(event.date).toLocaleDateString('de-AT', { day: '2-digit', month: 'long' })} {event.time && `| ${event.time} Uhr`}
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground hidden sm:block">
                    {clubs.find(c => c.id === event.club_id)?.name}
                  </span>
                </div>
              ))}
            </div>
            )}
          </div>
        </div>
      </div>

      {lightboxSrc && (
        <ImageLightbox src={lightboxSrc} alt="Event Bild" onClose={() => setLightboxSrc(null)} />
      )}
    </div>
  );
}
